/**
 * DynamicListInput — reusable dynamic add/remove list editor
 *
 * Used by: AdminVoicePracticeScenarios, AdminAnnounceTemplates, AdminPaxCommSituations
 *
 * @param {object[]} value  — current list items
 * @param {function} onChange — called with new list
 * @param {function} renderItem — (item, index, onChange) => ReactNode
 * @param {function} emptyItem — () => new item object
 * @param {string}   addLabel — button label
 * @param {number}   max — max items (default 10)
 * @param {number}   min — min items (default 0)
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

export default function DynamicListInput({
  value = [],
  onChange,
  renderItem,
  emptyItem,
  addLabel = 'Ekle',
  max = 10,
  min = 0,
}) {
  const handleAdd = () => {
    if (value.length >= max) return;
    onChange([...value, emptyItem()]);
  };

  const handleRemove = (index) => {
    if (value.length <= min) return;
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  const handleChange = (index, updated) => {
    const next = value.map((item, i) => (i === index ? updated : item));
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <AnimatePresence initial={false}>
        {value.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="relative border border-gray-200 rounded-xl p-4 bg-gray-50"
          >
            {renderItem(item, index, (updated) => handleChange(index, updated))}
            {value.length > min && (
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute top-3 right-3 text-rose-400 hover:text-rose-600 transition-colors"
                title="Kaldır"
              >
                <Trash2 size={16} />
              </button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {value.length < max && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="flex items-center gap-1.5 border-dashed"
        >
          <Plus size={14} />
          {addLabel}
        </Button>
      )}

      {value.length >= max && (
        <p className="text-xs text-gray-400">Maksimum {max} öğe eklenebilir.</p>
      )}
    </div>
  );
}
